import * as THREE from 'three';

import gsap from 'gsap';
import type { ThreeBase } from './useThreeBase';
import { VideoPlayer } from '~/utils/VideoPlayer';
import { getMuxThumbnail } from '~/utils/GetMuxThumbnail';

export interface ExperienceVideo {
  height: number;
  width: number;
  muxPlaybackId: string;
  streamingUrl: string;
}

export function useVideo(
  video: ExperienceVideo,
  thumbnail: string,
  context?: ThreeBase,
  videoIndex?: number,
) {
  const isVideoOpen = ref<boolean>(false);
  const textureLoader = new THREE.TextureLoader();

  const material = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
  });
  const frameMaterial = new THREE.MeshBasicMaterial({
    color: '#000000',
    transparent: false,
  });

  const geometry = new THREE.PlaneGeometry(3.25, 3.25);
  const plane = new THREE.Mesh(geometry, material);
  const edgesGeometry = new THREE.BoxGeometry(3.28, 3.35, 0.1);
  const frame = new THREE.Mesh(edgesGeometry, frameMaterial);
  frame.position.z = -0.052;
  frame.name = 'frame';
  plane.add(frame);

  const tlOnClick = gsap.timeline({
    paused: true,
    default: {
      duration: 0.34,
    },
  });

  const videoScale = {
    x: 1,
    y: 1,
  };

  document.addEventListener(VideoPlayer.eventVideoClose, onVideoOverlayClose);

  if (video.width && video.height) {
    const thumbnailUrl = thumbnail
      ? thumbnail
      : getMuxThumbnail(video.muxPlaybackId);

    textureLoader.load(thumbnailUrl, function (texture) {
      material.map = texture;
      material.needsUpdate = true;

      videoScale.y = (1 / (video.width as number)) * (video.height as number);

      plane.scale.set(1, videoScale.y, 1);

      tlOnClick.fromTo(
        material,
        {
          opacity: 1,
        },
        {
          opacity: 0,
        },
      );
    });
  }

  const buttonGeometry = new THREE.PlaneGeometry(0.42, 0.42);
  const buttonTexture = textureLoader.load('/icons/play-button.png');
  buttonTexture.colorSpace = THREE.SRGBColorSpace;
  const buttonMaterial = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    map: buttonTexture,
  });
  const button = new THREE.Mesh(buttonGeometry, buttonMaterial);

  return {
    plane,
    button,
    play,
  };

  function play() {
    if (video) {
      isVideoOpen.value = true;
      VideoPlayer.dispatchVideoStart(video, videoIndex);
      if (!context) return;
      context.setParallaxEnabled(false);

      tlOnClick.play(0);
    }
  }

  function onVideoOverlayClose() {
    if (isVideoOpen.value) {
      isVideoOpen.value = false;

      tlOnClick.reverse();

      if (!context) return;
      context.resetCameraPosition();
      context.setParallaxEnabled(true);
    }
  }
}
